import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ASSETS } from '@/lib/config';
import { STRINGS } from '@/strings/strings';

type BuilderErrorBannerProps =
  | { variant: 'limit-trial' }
  | { variant: 'limit-subscription-month'; formattedEndDate: string }
  | { variant: 'limit-subscription-expired'; formattedEndDate: string }
  | { variant: 'no-child' }
  | {
      variant: 'email-unverified';
      onResend: () => void;
      resendLoading: boolean | undefined;
      resendSuccess?: boolean;
      rateLimited?: boolean;
    };

export function BuilderErrorBanner(props: BuilderErrorBannerProps) {
  if (props.variant === 'limit-trial') {
    return <LimitTrialBanner />;
  }
  if (props.variant === 'limit-subscription-month') {
    return <LimitSubscriptionMonthBanner formattedEndDate={props.formattedEndDate} />;
  }
  if (props.variant === 'limit-subscription-expired') {
    return <LimitSubscriptionExpiredBanner formattedEndDate={props.formattedEndDate} />;
  }
  if (props.variant === 'no-child') {
    return <NoChildBanner />;
  }
  return (
    <EmailUnverifiedBanner
      onResend={props.onResend}
      resendLoading={props.resendLoading}
      resendSuccess={props.resendSuccess}
      rateLimited={props.rateLimited}
    />
  );
}

// ── Shared layout for the 3 limit-* variants ──────────────────
interface LimitShellProps {
  title: string;
  body: string;
  children: React.ReactNode;
}

function LimitShell({ title, body, children }: LimitShellProps) {
  return (
    <div className="rounded-xl border border-dream-navy/10 bg-dream-white p-4">
      <div className="flex items-start gap-3">
        <img
          src={ASSETS.kaptnKuschelSittingImage}
          alt=""
          loading="lazy"
          className="shrink-0 rounded-lg object-contain drop-shadow-md"
          style={{ width: '72px', height: '72px' }}
        />
        <div className="min-w-0 flex-1">
          <h3 className="text-sm leading-snug font-semibold text-dream-navy">{title}</h3>
          <p className="mt-1 text-sm leading-relaxed text-dream-navy/80">{body}</p>
        </div>
      </div>
      <div className="mt-3 flex flex-col gap-2">{children}</div>
    </div>
  );
}

// ── CTA button presets ────────────────────────────────────────
const goldCtaClass =
  'flex h-11 w-full items-center justify-center rounded-lg bg-dream-gold text-base font-bold text-dream-navy transition-colors hover:bg-dream-gold/90';

const outlineCtaClass =
  'flex h-11 w-full items-center justify-center rounded-lg border-[1.5px] border-dream-navy bg-transparent text-base font-semibold text-dream-navy transition-colors hover:bg-dream-navy/5';

const textCtaClass =
  'flex h-10 w-full items-center justify-center text-sm font-medium text-dream-navy/80 underline-offset-4 transition-colors hover:text-dream-navy hover:underline';

// ── Variant: limit-trial (Szenario 1) ─────────────────────────
function LimitTrialBanner() {
  const S = STRINGS.builder.errorBanners.limitTrial;
  return (
    <LimitShell title={S.title} body={S.body}>
      <Link to="/account/subscription" className={goldCtaClass}>
        {S.ctaPrimary}
      </Link>
      <Link to="/account/subscription#packs" className={outlineCtaClass}>
        {S.ctaSecondary}
      </Link>
    </LimitShell>
  );
}

// ── Variant: limit-subscription-month (Szenario 2) ────────────
interface LimitSubscriptionMonthBannerProps {
  formattedEndDate: string;
}

function LimitSubscriptionMonthBanner({ formattedEndDate }: LimitSubscriptionMonthBannerProps) {
  const S = STRINGS.builder.errorBanners.limitSubscriptionMonth;
  const body = S.body.replace('{date}', formattedEndDate);
  return (
    <LimitShell title={S.title} body={body}>
      <Link to="/account/subscription#packs" className={goldCtaClass}>
        {S.ctaPrimary}
      </Link>
      <Link to="/account/subscription#upgrade" className={outlineCtaClass}>
        {S.ctaSecondary}
      </Link>
      <Link to="/account/subscription" className={textCtaClass}>
        {S.ctaTertiary}
      </Link>
    </LimitShell>
  );
}

// ── Variant: limit-subscription-expired (Szenario 3) ──────────
interface LimitSubscriptionExpiredBannerProps {
  formattedEndDate: string;
}

function LimitSubscriptionExpiredBanner({ formattedEndDate }: LimitSubscriptionExpiredBannerProps) {
  const S = STRINGS.builder.errorBanners.limitSubscriptionExpired;
  const body = S.body.replace('{date}', formattedEndDate);
  return (
    <LimitShell title={S.title} body={body}>
      <Link to="/account/subscription" className={goldCtaClass}>
        {S.ctaPrimary}
      </Link>
      <Link to="/account/subscription#packs" className={outlineCtaClass}>
        {S.ctaSecondary}
      </Link>
    </LimitShell>
  );
}

// ── Variant: no-child (unchanged from 5.1) ────────────────────
function NoChildBanner() {
  const S = STRINGS.builder.errorBanners.noChild;
  return (
    <div className="rounded-xl border border-dream-navy/10 bg-dream-white p-4">
      <h3 className="text-sm leading-snug font-semibold text-dream-navy">{S.title}</h3>
      <p className="mt-1 text-sm leading-relaxed text-dream-navy/80">{S.body}</p>
      <Link
        to="/account/child_profile?mode=new"
        className="mt-3 flex h-11 w-full items-center justify-center rounded-lg bg-dream-navy text-base font-semibold text-dream-white transition-colors hover:bg-dream-navy/90"
      >
        {S.cta}
      </Link>
    </div>
  );
}

// ── Variant: email-unverified (unchanged from 5.1) ────────────
interface EmailUnverifiedBannerProps {
  onResend: () => void;
  resendLoading: boolean | undefined;
  resendSuccess: boolean | undefined;
  rateLimited: boolean | undefined;
}

function EmailUnverifiedBanner({
  onResend,
  resendLoading,
  resendSuccess,
  rateLimited,
}: EmailUnverifiedBannerProps) {
  const S = STRINGS.builder.errorBanners.emailUnverified;
  const body = resendSuccess ? S.resendSuccessBody : S.body;
  return (
    <div className="rounded-xl border border-dream-navy/10 bg-dream-white p-4">
      <h3 className="text-sm leading-snug font-semibold text-dream-navy">{S.title}</h3>
      <p className="mt-1 text-sm leading-relaxed text-dream-navy/80">{body}</p>
      {!resendSuccess && (
        <>
          <Button
            type="button"
            onClick={onResend}
            disabled={resendLoading || rateLimited}
            className="mt-3 h-11 w-full bg-dream-navy text-base font-semibold text-dream-white hover:bg-dream-navy/90 disabled:opacity-60"
          >
            {resendLoading ? (
              <>
                <Loader2 aria-hidden className="mr-2 h-4 w-4 animate-spin" />
                {S.resendLoadingLabel}
              </>
            ) : (
              S.cta
            )}
          </Button>
          {rateLimited && (
            <p className="mt-2 text-center text-xs text-dream-navy/60">{S.rateLimitedHint}</p>
          )}
        </>
      )}
    </div>
  );
}
